import { useCallback, useEffect, useRef, useState } from "react"

import { getRouteOrganizationBearerHeaders } from "@/utils/auth/organization-token"

import {
  FINDINGS_API_PATH,
  investigationInstant,
  parseInvestigationActionResponse,
  parseInvestigationActivity,
  parseInvestigationEvidence,
  parseInvestigationFindings,
  type InvestigationEvidenceItem,
  type InvestigationEvidenceLoad,
  type InvestigationFinding,
  type InvestigationFindingAction,
  type InvestigationFindingActivity,
  type InvestigationFindingsPage,
  type InvestigationFindingsSnapshot,
} from "../utils/investigationFindings"
import { loadExactInvestigationFinding } from "./loadExactInvestigationFinding"
import {
  useFindingsRefreshQueue,
  type InvestigationFindingRefresh,
} from "./useFindingsRefreshQueue"

// Follow empty status-filtered pages until exhaustion or this bound; a live cursor means incomplete history.
const MAX_FINDINGS_PAGE_REQUESTS = 10
const MAX_EVIDENCE_PAGE_REQUESTS = 10

type InvestigationFindingsStatus =
  | "loading"
  | "ready"
  | "error"
  // The proxy relayed Sofia's undisclosed 404: this org has no findings surface.
  // The Home section renders nothing at all in this state.
  | "unavailable"

interface UseInvestigationFindingsArgs {
  enabled: boolean
  focusedFindingId?: string
  status?: "all" | InvestigationFinding["status"]
  view?: "briefing"
  workspaceId: string | null
  organizationSlug?: string | null
}

type FindingsLoadResult =
  | { kind: "error" }
  | { kind: "unavailable" }
  | { kind: "ready"; snapshot: InvestigationFindingsSnapshot }

// Naming the route organization keeps cross-org browsing scoped to the browsed org.
function applyOrganizationParam(
  query: URLSearchParams,
  organizationSlug: string | null | undefined,
) {
  if (organizationSlug) {
    query.set("organization", organizationSlug)
  }
}

function findingsPageUrl(
  workspaceId: string,
  pageToken: string | null,
  status: UseInvestigationFindingsArgs["status"],
  view: UseInvestigationFindingsArgs["view"],
  organizationSlug: string | null | undefined,
) {
  const query = new URLSearchParams({ workspace: workspaceId })
  if (status) {
    query.set("status", status)
  }
  if (pageToken !== null) {
    query.set("page_token", pageToken)
  }
  if (view) {
    query.set("view", view)
  }
  applyOrganizationParam(query, organizationSlug)
  return `${FINDINGS_API_PATH}?${query.toString()}`
}

// Resolves only once the whole follow finishes, so the caller commits a complete
// snapshot or nothing at all.
async function loadFindingsSnapshot(
  workspaceId: string,
  signal: AbortSignal,
  status: UseInvestigationFindingsArgs["status"],
  view: UseInvestigationFindingsArgs["view"],
  organizationSlug: string | null | undefined,
  focusedFindingId?: string,
): Promise<FindingsLoadResult> {
  const findings: InvestigationFinding[] = []
  let firstPage: InvestigationFindingsPage | null = null
  let pageToken: string | null = null

  for (let request = 0; request < MAX_FINDINGS_PAGE_REQUESTS; request += 1) {
    const response = await fetch(
      findingsPageUrl(workspaceId, pageToken, status, view, organizationSlug),
      {
        cache: "no-store",
        headers: await getRouteOrganizationBearerHeaders(),
        method: "GET",
        signal,
      },
    )
    if (response.status === 404) {
      return { kind: "unavailable" }
    }
    if (!response.ok) {
      return { kind: "error" }
    }
    const page = parseInvestigationFindings(await response.json())
    if (page === null) {
      return { kind: "error" }
    }
    // Freshness and workspace identity describe the query, not the page.
    firstPage ??= page
    findings.push(...page.findings)
    pageToken = page.nextPageToken
    if (view === "briefing" || pageToken === null) {
      break
    }
  }

  if (
    pageToken !== null &&
    focusedFindingId &&
    !findings.some((finding) => finding.findingId === focusedFindingId)
  ) {
    const exact = await loadExactInvestigationFinding({
      findingId: focusedFindingId,
      workspaceId,
      organizationSlug,
      signal,
    })
    if (exact) {
      findings.push(exact)
    }
  }

  if (pageToken !== null && findings.length === 0) {
    return { kind: "error" }
  }

  // The server's creation-order cursor guarantees last-seen ordering only within a page.
  if (view !== "briefing") {
    findings.sort(
      (a, b) =>
        investigationInstant(b.lastSeen) - investigationInstant(a.lastSeen),
    )
  }

  return {
    kind: "ready",
    snapshot: {
      briefing: firstPage?.briefing,
      findings,
      freshness: firstPage?.freshness ?? {
        conversation: null,
        simulation: null,
      },
      truncated: firstPage?.briefing?.truncated ?? pageToken !== null,
      workspaceId: firstPage?.workspaceId ?? workspaceId,
    },
  }
}

export function useInvestigationFindings({
  enabled,
  focusedFindingId,
  status: requestedStatus,
  view,
  workspaceId,
  organizationSlug,
}: UseInvestigationFindingsArgs): {
  patchFinding: (finding: InvestigationFinding) => void
  refetch: () => void
  refreshFinding: (findingId: string) => Promise<InvestigationFindingRefresh>
  snapshot: InvestigationFindingsSnapshot | null
  status: InvestigationFindingsStatus
} {
  const [snapshot, setSnapshot] =
    useState<InvestigationFindingsSnapshot | null>(null)
  const [status, setStatus] = useState<InvestigationFindingsStatus>("loading")
  const { refetch, refreshFinding, refreshToken, settleFindingRefreshes } =
    useFindingsRefreshQueue()
  const snapshotRef = useRef<InvestigationFindingsSnapshot | null>(null)
  const loadedFocusRef = useRef<string | undefined>(focusedFindingId)
  snapshotRef.current = snapshot

  useEffect(() => {
    const snapshotMatchesWorkspace =
      snapshotRef.current?.workspaceId === workspaceId
    if (!enabled || !workspaceId) {
      setSnapshot(null)
      setStatus("loading")
      settleFindingRefreshes(null, refreshToken)
      return
    }
    if (!snapshotMatchesWorkspace) {
      setSnapshot(null)
    }
    const controller = new AbortController()
    let cancelled = false

    const load = async () => {
      setStatus((current) =>
        snapshotMatchesWorkspace &&
        loadedFocusRef.current === focusedFindingId &&
        current === "ready"
          ? current
          : "loading",
      )
      try {
        const result = await loadFindingsSnapshot(
          workspaceId,
          controller.signal,
          requestedStatus,
          view,
          organizationSlug,
          focusedFindingId,
        )
        if (cancelled) {
          return
        }
        if (result.kind === "unavailable") {
          setSnapshot(null)
          setStatus("unavailable")
          settleFindingRefreshes(null, refreshToken)
          return
        }
        if (result.kind === "error") {
          setStatus("error")
          settleFindingRefreshes(null, refreshToken)
          return
        }
        loadedFocusRef.current = focusedFindingId
        setSnapshot(result.snapshot)
        setStatus("ready")
        settleFindingRefreshes(result.snapshot, refreshToken)
      } catch {
        if (!cancelled) {
          setStatus("error")
          settleFindingRefreshes(null, refreshToken)
        }
      }
    }

    void load()
    return () => {
      cancelled = true
      controller.abort()
    }
  }, [
    enabled,
    focusedFindingId,
    requestedStatus,
    view,
    workspaceId,
    organizationSlug,
    refreshToken,
    settleFindingRefreshes,
  ])

  // Patch the server's copy before refetch, so a failed refresh cannot leave stale writes actionable.
  const patchFinding = useCallback(
    (updated: InvestigationFinding) => {
      setSnapshot((current) => {
        if (current?.workspaceId !== workspaceId) {
          return current
        }
        let replaced = false
        const findings = current.findings.map((finding) => {
          if (finding.findingId !== updated.findingId) {
            return finding
          }
          replaced = true
          return updated
        })
        return replaced ? { ...current, findings } : current
      })
    },
    [workspaceId],
  )

  const currentWorkspaceSnapshot =
    enabled && workspaceId && snapshot?.workspaceId === workspaceId
      ? snapshot
      : null
  const isShowingStaleSnapshot =
    snapshot !== null && currentWorkspaceSnapshot === null

  return {
    patchFinding,
    refetch,
    refreshFinding,
    snapshot: currentWorkspaceSnapshot,
    status: isShowingStaleSnapshot ? "loading" : status,
  }
}

interface InvestigationFindingActionResult {
  finding: InvestigationFinding | null
  kind: "applied" | "conflict" | "error"
}

export async function applyInvestigationFindingAction(args: {
  action: InvestigationFindingAction
  expectedVersion: number
  findingId: string
  note?: string
  organizationSlug?: string | null
  remediationLabel?: string
  remediationUrl?: string
  workspaceId: string
}): Promise<InvestigationFindingActionResult> {
  try {
    const response = await fetch(
      `${FINDINGS_API_PATH}/${encodeURIComponent(args.findingId)}/actions`,
      {
        body: JSON.stringify({
          action: args.action,
          expected_version: args.expectedVersion,
          ...(args.note ? { note: args.note } : {}),
          ...(args.organizationSlug
            ? { organization: args.organizationSlug }
            : {}),
          ...(args.remediationLabel
            ? { remediation_label: args.remediationLabel }
            : {}),
          ...(args.remediationUrl
            ? { remediation_url: args.remediationUrl }
            : {}),
          workspace: args.workspaceId,
        }),
        headers: {
          "Content-Type": "application/json",
          ...(await getRouteOrganizationBearerHeaders()),
        },
        method: "POST",
      },
    )
    if (response.status === 409) {
      return { finding: null, kind: "conflict" }
    }
    if (!response.ok) {
      return { finding: null, kind: "error" }
    }
    const finding = parseInvestigationActionResponse(await response.json())
    // An HTTP success with no parseable confirmation is not proof Sofia recorded the
    // action, so treat it as a failure instead of toasting success.
    if (finding === null) {
      return { finding: null, kind: "error" }
    }
    return { finding, kind: "applied" }
  } catch {
    return { finding: null, kind: "error" }
  }
}

function findingDetailPageUrl(
  findingId: string,
  workspaceId: string,
  pageToken: string | null,
  organizationSlug: string | null | undefined,
  collection: "activity" | "evidence",
) {
  const query = new URLSearchParams({ workspace: workspaceId })
  if (pageToken !== null) {
    query.set("page_token", pageToken)
  }
  applyOrganizationParam(query, organizationSlug)
  return `${FINDINGS_API_PATH}/${encodeURIComponent(findingId)}/${collection}?${query.toString()}`
}

interface InvestigationActivityLoad {
  items: InvestigationFindingActivity[]
  // A live server cursor means unvisited events remain.
  truncated: boolean
}

// Sofia pages activity oldest first, so follow the token to the end before the
// timeline renders. A cap-stopped follow with nothing loaded errors instead of
// claiming an empty history, matching the findings loader.
export async function loadInvestigationActivity(args: {
  findingId: string
  organizationSlug?: string | null
  workspaceId: string
}): Promise<InvestigationActivityLoad | null> {
  try {
    const items: InvestigationFindingActivity[] = []
    let pageToken: string | null = null
    for (let request = 0; request < MAX_EVIDENCE_PAGE_REQUESTS; request += 1) {
      const response = await fetch(
        findingDetailPageUrl(
          args.findingId,
          args.workspaceId,
          pageToken,
          args.organizationSlug,
          "activity",
        ),
        {
          cache: "no-store",
          headers: await getRouteOrganizationBearerHeaders(),
          method: "GET",
        },
      )
      if (!response.ok) {
        return null
      }
      const page = parseInvestigationActivity(await response.json())
      if (page === null) {
        return null
      }
      items.push(...page.items)
      pageToken = page.nextPageToken
      if (pageToken === null) {
        return { items, truncated: false }
      }
    }
    if (items.length === 0) {
      return null
    }
    return { items, truncated: true }
  } catch {
    return null
  }
}

// The toggle advertises the finding's full evidence count, so follow the token to the
// end rather than showing one page against a larger promise. The row states the
// shortfall when the bound stops the follow early.
export async function loadInvestigationEvidence(args: {
  basePath: string
  findingId: string
  organizationSlug?: string | null
  workspaceId: string
}): Promise<InvestigationEvidenceLoad | null> {
  try {
    const items: InvestigationEvidenceItem[] = []
    let pageToken: string | null = null

    for (let request = 0; request < MAX_EVIDENCE_PAGE_REQUESTS; request += 1) {
      const response = await fetch(
        findingDetailPageUrl(
          args.findingId,
          args.workspaceId,
          pageToken,
          args.organizationSlug,
          "evidence",
        ),
        {
          cache: "no-store",
          headers: await getRouteOrganizationBearerHeaders(),
          method: "GET",
        },
      )
      if (!response.ok) {
        return null
      }
      const page = parseInvestigationEvidence(
        await response.json(),
        args.basePath,
      )
      if (page === null) {
        return null
      }
      items.push(...page.items)
      pageToken = page.nextPageToken
      if (pageToken === null) {
        return { items, truncated: false }
      }
    }

    return { items, truncated: true }
  } catch {
    return null
  }
}
